import type { BrandContextDocApplyWhen, BrandContextDocType } from "../../../shared/types.ts";

export type ContextDocTemplate = {
  id: string;
  label: string;
  doc_type: BrandContextDocType;
  name: string;
  surface: string | null;
  apply_when: BrandContextDocApplyWhen[];
  usage_prompt?: string;
  content: string;
};

export const CONTEXT_DOC_TEMPLATES: Record<BrandContextDocType, ContextDocTemplate[]> = {
  persona: [
    {
      id: "persona-icp",
      label: "ICP persona",
      doc_type: "persona",
      name: "ICP persona",
      surface: null,
      apply_when: ["all"],
      usage_prompt: "Use this to ground who the brand is speaking to before drafting or reviewing content.",
      content: "## Who they are\n- Role:\n- Company stage:\n- Context they operate in:\n\n## What they care about\n- \n\n## What they reject\n- \n",
    },
    {
      id: "persona-buyer-committee",
      label: "Buyer committee",
      doc_type: "persona",
      name: "Buyer committee",
      surface: null,
      apply_when: ["writing", "reviewing"],
      usage_prompt: "Use this when content needs to acknowledge multiple stakeholders instead of a single champion.",
      content: "## Members\n- Economic buyer:\n- Technical evaluator:\n- End user:\n\n## Shared objections\n- \n",
    },
  ],
  voice_guide: [
    {
      id: "voice-guide-core",
      label: "Brand tone guide",
      doc_type: "voice_guide",
      name: "Brand tone guide",
      surface: null,
      apply_when: ["all"],
      usage_prompt: "Apply these rules as the default voice system unless a surface-specific doc overrides them.",
      content: "## Always sound like\n- \n\n## Never sound like\n- \n\n## Preferred vocabulary\n- \n",
    },
    {
      id: "voice-guide-claims",
      label: "Claims discipline",
      doc_type: "voice_guide",
      name: "Claims discipline",
      surface: null,
      apply_when: ["reviewing"],
      usage_prompt: "Use this when reviewing confidence, evidence, and overclaim risk in approved drafts.",
      content: "## Allowed claims\n- \n\n## Claims that require evidence\n- \n\n## Words to avoid\n- \n",
    },
  ],
  tone: [
    {
      id: "tone-founder",
      label: "Founder note tone",
      doc_type: "tone",
      name: "Founder note tone",
      surface: null,
      apply_when: ["writing"],
      usage_prompt: "Use this for first-person updates, founder notes, and reflective operator commentary.",
      content: "## Tone anchors\n- \n\n## Sentence rhythm\n- \n\n## Avoid\n- \n",
    },
    {
      id: "tone-product",
      label: "Product launch tone",
      doc_type: "tone",
      name: "Product launch tone",
      surface: null,
      apply_when: ["writing", "reviewing"],
      usage_prompt: "Use this when announcing launches, shipping notes, or product updates.",
      content: "## Emotional register\n- \n\n## Product proof expected\n- \n\n## CTA style\n- \n",
    },
  ],
  surface_context: [
    {
      id: "surface-linkedin",
      label: "LinkedIn surface rules",
      doc_type: "surface_context",
      name: "LinkedIn surface rules",
      surface: "linkedin_personal",
      apply_when: ["writing", "reviewing"],
      usage_prompt: "Use this only for LinkedIn personal posts and their review loops.",
      content: "## Format rules\n- \n\n## What earns attention\n- \n\n## What gets ignored\n- \n",
    },
    {
      id: "surface-youtube",
      label: "YouTube script rules",
      doc_type: "surface_context",
      name: "YouTube script rules",
      surface: "youtube_brand_script",
      apply_when: ["writing", "reviewing"],
      usage_prompt: "Use this only for YouTube scripting and review.",
      content: "## Openings\n- \n\n## Retention beats\n- \n\n## CTA rules\n- \n",
    },
  ],
  custom: [
    {
      id: "custom-review",
      label: "Review checklist",
      doc_type: "custom",
      name: "Review checklist",
      surface: null,
      apply_when: ["reviewing"],
      usage_prompt: "Use this as a brand-specific checklist during operator review.",
      content: "## Check before approval\n- \n\n## Reject if\n- \n",
    },
    {
      id: "custom-campaign",
      label: "Campaign context",
      doc_type: "custom",
      name: "Campaign context",
      surface: null,
      apply_when: ["writing"],
      usage_prompt: "Use this when a live campaign needs extra constraints beyond strategy and voice.",
      content: "## Campaign context\n- \n\n## Mandatory mentions\n- \n\n## Avoid in this campaign\n- \n",
    },
  ],
};
